import React, {useImperativeHandle, useRef} from 'react';
import StyledNavLink from 'components/atoms/Links/NavLink/NavLink.styles';
import utils from 'helpers/utils';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import {useNavLink} from 'helpers/hooks/links';
import dom from 'helpers/dom';
import {withMemo} from 'helpers/wrapper';

const NavLink = withMemo(React.forwardRef((props, ref) => {
  const {
    to, replace,
    state, relative,
    preventScrollReset,
    keepSearchParams,
    resetSearchParams,
    reloadDocument,
    end, target, onClick,
    ...innerProps
  } = useComponentProps({
    end: true,
    ...props
  }, 'NavLink', {static: ['active']});

  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  // component code
  const [handleClick, active, href] = useNavLink({
    to, replace,
    state, relative,
    preventScrollReset,
    keepSearchParams,
    resetSearchParams,
    reloadDocument,
    end, target, onClick,
  });

  const clickable = Boolean(handleClick || href);

  const handleKeyDown = (e) => {
    if (handleClick && e.target === innerRef.current && clickable) {
      if (e.code === 'Space' || (e.code === 'Enter' && !dom.isNativeButton(e.target))) {
        e.target?.click();
        e.preventDefault();
      }
    }
  }

  innerProps.tabIndex = innerProps.tabIndex ?? (clickable ? 0 : -1);
  if (utils.isFunction(innerProps.children)) {
    innerProps.children = innerProps.children(active);
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {...innerProps, active});

  return <StyledNavLink ref={innerRef} {...innerProps}
                        href={href ?? innerProps.href ?? ''}
                        target={target}
                        onClick={handleClick}
                        onKeyDown={handleKeyDown}>
    {innerProps.children}
  </StyledNavLink>
}));

NavLink.propTypes = {
  to: PropTypes.string,
  relative: PropTypes.string,
  replace: PropTypes.bool,
  end: PropTypes.bool,
  preventScrollReset: PropTypes.bool,
  keepSearchParams: PropTypes.bool,
  resetSearchParams: PropTypes.bool,
  reloadDocument: PropTypes.bool,
  state: PropTypes.object,
  target: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default NavLink;
